import { MatriculaAlunoSchema } from "@/schema/matriculaAlunoSchema";
import { Orcamento as DocumentoOrcamento, DisciplinaTurma } from "@/pages/private/Aluno/Cadastro/Reports/Orcamento";
import { ResultadoOrcamento, SalvarOrcamentoResponse } from "@/services/orcamentoPublico";
import { formatarMoeda } from "./formato";

type Props = {
  resposta: SalvarOrcamentoResponse;
  interessadoNome: string;
  interessadoEmail: string;
  alunoNome: string;
  onNovoOrcamento: () => void;
};

/**
 * Fecho do fluxo: o número do orçamento e o documento pronto para imprimir. O
 * PDF é gerado no próprio navegador (html2canvas + jsPDF), pelo mesmo
 * componente que a secretaria usa — nenhum endpoint de impressão é necessário.
 */
export function Sucesso({
  resposta,
  interessadoNome,
  interessadoEmail,
  alunoNome,
  onNovoOrcamento,
}: Props) {
  const orcamento = resposta.orcamento;
  const numero = resposta.idOrcamento ?? resposta.id;
  const primeiroNome = interessadoNome.trim().split(/\s+/)[0];

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <section className="op-panel" style={{ overflow: "hidden" }}>
        <div
          style={{
            padding: "30px 26px",
            textAlign: "center",
            background: "linear-gradient(135deg, var(--op-indigo-wash), rgba(42,175,192,.11))",
            borderBottom: "1px solid var(--op-line)",
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 46,
              height: 46,
              borderRadius: "50%",
              background: "var(--op-indigo)",
              color: "#fff",
              display: "grid",
              placeItems: "center",
              margin: "0 auto 14px",
              fontSize: 22,
              fontWeight: 800,
            }}
          >
            ✓
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 800, margin: 0 }}>
            Tudo certo, {primeiroNome}!
          </h2>
          <p style={{ color: "var(--op-muted)", margin: "8px 0 0", fontSize: 15.5 }}>
            Guarde o número abaixo — é por ele que a secretaria localiza sua proposta.
          </p>

          <div
            style={{
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              marginTop: 18,
              padding: "12px 26px",
              borderRadius: 12,
              background: "var(--op-surface)",
              border: "1.5px dashed var(--op-line-strong)",
            }}
          >
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: ".17em",
                textTransform: "uppercase",
                color: "var(--op-faint)",
              }}
            >
              Orçamento nº
            </span>
            <span
              className="op-num"
              style={{ fontWeight: 800, fontSize: 34, lineHeight: 1.1, color: "var(--op-indigo)" }}
            >
              {numero}
            </span>
          </div>
        </div>

        <div style={{ padding: "22px 26px", display: "grid", gap: 16 }}>
          <ul style={{ display: "grid", gap: 9, fontSize: 14.5, color: "var(--op-muted)", margin: 0, paddingLeft: 20 }}>
            <li>Sua proposta já está registrada no sistema da unidade {orcamento.unidadeNome}.</li>
            <li>A secretaria entra em contato pelo {interessadoEmail} ou pelo celular informado.</li>
            <li>Quer adiantar? Leve o número do orçamento na recepção.</li>
          </ul>
          <div style={{ fontSize: 15, fontWeight: 700 }}>
            Total da proposta: {formatarMoeda(orcamento.totalFinal)}
          </div>
          <div>
            <button type="button" className="op-btn op-btn-ghost" onClick={onNovoOrcamento}>
              Fazer outro orçamento
            </button>
          </div>
        </div>
      </section>

      <section className="op-panel" style={{ overflow: "hidden" }}>
        <div
          style={{
            padding: "13px 16px",
            borderBottom: "1px solid var(--op-line)",
            background: "var(--op-surface-2)",
          }}
        >
          <span style={{ fontWeight: 800, fontSize: 15 }}>Orçamento para impressão</span>
        </div>
        <div style={{ padding: 18 }}>
          <DocumentoOrcamento
            matriculaAluno={montarDocumento(orcamento, interessadoNome, alunoNome)}
            disciplinasTurma={montarGrade(orcamento)}
          />
        </div>
      </section>
    </div>
  );
}

/** Grade completa da turma, com o X marcando o que foi escolhido. */
function montarGrade(orcamento: ResultadoOrcamento): DisciplinaTurma[] {
  return orcamento.linhas
    .map((linha) => ({ cod: linha.codigo, nome: linha.nome }))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
}

/**
 * O documento de impressão é compartilhado com a tela de matrícula e espera o
 * shape do formulário de lá. Aqui ele é montado a partir do que a API devolveu.
 */
function montarDocumento(
  orcamento: ResultadoOrcamento,
  interessadoNome: string,
  alunoNome: string
): MatriculaAlunoSchema {
  const disciplinas = orcamento.linhas
    .filter((linha) => linha.selecionado)
    .map((linha) => ({
      codEspecificaServico: linha.codigo,
      disciplinaId: linha.disciplinaId,
      tipo: linha.tipo,
      nome: linha.nome,
      valor: linha.valor,
      percDesconto: linha.percDesconto,
      valorDesconto: linha.valorDesconto,
      valorFinal: linha.valorFinal,
    }));

  if (orcamento.matriculaCodigo) {
    disciplinas.push({
      codEspecificaServico: orcamento.matriculaCodigo,
      disciplinaId: 0,
      tipo: "MATRICULA",
      nome: orcamento.matriculaNome ?? "Matrícula",
      valor: orcamento.matriculaValor,
      percDesconto: 0,
      valorDesconto: 0,
      valorFinal: orcamento.matriculaValor,
    });
  }

  return {
    tipoMatricula: "5",
    statusMatriculaId: 5,
    responsavel: { pessoaResponsavel: { nome: interessadoNome } },
    estudante: { pessoaEstudante: { nome: alunoNome } },
    disciplinas,
    produtos: [],
    pagamentos: [],
    cheques: [],
    turmaId: orcamento.turmaId,
    turmaNome: orcamento.turmaNome,
    unidade: { id: orcamento.unidadeId, nome: orcamento.unidadeNome },
    // O rodapé do documento imprime "Orçamento gerado por": no fluxo público
    // quem gerou foi o próprio site.
    usuarioNome: "Site",
  } as MatriculaAlunoSchema;
}

export default Sucesso;
